import dayjs, { Dayjs } from 'dayjs';

import { DateTimeIso } from '../types';

const locale = 'en-GB';

export const MS_IN_A_DAY = 86400000;

const ISO_8601 = 'YYYY-MM-DD[T]HH:mm:ss.SSS[Z]';

export const dates = {
  isValidIsoFormat: (value: dayjs.ConfigType) => {
    return dayjs(value, ISO_8601, true).isValid();
  },

  formatShort: (date: Date) => {
    return date
      ?.toLocaleDateString(locale, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      })
      .replace(/ /g, ' ');
  },

  formatDateTimeInterwill(date: Date | string) {
    return dayjs(date).format('YYYY-MM-DD HH:mm');
  },

  formatDateTime(date: Date | string) {
    return dayjs(date).format('MM.DD.YYYY - HH:mm');
  },

  formatDate(date: Date | string) {
    return dayjs(date).format('YYYY-MM-DD');
  },

  formatDate2(date: Date | string) {
    return dayjs(date).format('DD.MM.YYYY');
  },

  formatShortDate(date: Date | string) {
    return dayjs(date).format('DD MMM');
  },

  formatLongDate(date: Date | string) {
    return dayjs(date).format('DD MMMM YYYY');
  },

  formatLongDate2(date: Date | string) {
    return dayjs(date).format('MMMM DD, YYYY');
  },

  formatTime(date: Date | string) {
    return dayjs(date).format('HH:mm');
  },

  formatDateSeparator(date: Date | string) {
    return dayjs(date).format('DD MMMM');
  },

  formatMask: (date: Date, options?: Intl.DateTimeFormatOptions) => {
    if (!(date instanceof Date) || isNaN(date.getTime())) {
      throw new TypeError('Invalid date');
    }

    return new Intl.DateTimeFormat(locale, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      ...options,
    }).format(date);
  },

  formatLong: (date: string | Date) => {
    return dayjs(date).format('DD MMMM YYYY');
  },

  // Return timestamp for Date 00:00:00
  getDateTs: (ts: number) => {
    const dt = new Date(ts);

    dt.setHours(0);
    dt.setMinutes(0);
    dt.setSeconds(0);
    dt.setMilliseconds(0);

    return dt.getTime();
  },

  timestampToDays: (days: number | undefined) => {
    return days ? days / MS_IN_A_DAY : 0;
  },

  formatSecondsToMmSs: (seconds: number): string => {
    const secondsChecked = Math.max(0, seconds);
    const minutes = Math.floor(secondsChecked / 60);
    const secs = secondsChecked % 60;

    const paddedMinutes = String(minutes).padStart(2, '0');
    const paddedSeconds = String(secs).padStart(2, '0');

    return `${paddedMinutes}:${paddedSeconds}`;
  },

  diffDate: (date1: Date, date2: Date) => {
    return dayjs(date1).diff(date2, 'day');
  },

  dayjsToIsoDateTime: (date: Dayjs): DateTimeIso => {
    return date.toISOString() as DateTimeIso;
  },
};

export const soweDates = {
  date(ts: number | string) {
    const options: Intl.DateTimeFormatOptions = {
      // year: 'numeric',
      year: '2-digit',
      month: '2-digit',
      day: '2-digit',
      // dateStyle: dateStyle
    };

    return new Date(ts).toLocaleDateString('ru', options); // TODO localization
  },

  time(ts: number | string) {
    const options: Intl.DateTimeFormatOptions = {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      // timeStyle: 'short' // full long medium short
    };

    return new Date(ts).toLocaleTimeString('ru', options); // TODO localization
  },

  datetime(ts: number | string) {
    return `${soweDates.time(ts)} ${soweDates.date(ts)}`;
  },

  formatSoweDate: (value: Date) => {
    let formattedDate;

    try {
      formattedDate = new Intl.DateTimeFormat('en', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      }).format(value);
    } catch (error) {
      return '-';
    }

    return formattedDate;
  },
};
